/**
 * Session de l'agent côté navigateur.
 *
 * Le jeton est rangé en `sessionStorage`, pas en `localStorage` : les postes des
 * administrations sont partagés, et une session qui survit à la fermeture de
 * l'onglet est une session qu'un autre agent récupère. La contrepartie — se
 * reconnecter en ouvrant un nouvel onglet — est le bon arbitrage sur un poste
 * mutualisé.
 *
 * Les rôles sont lus dans la charge du JWT. Ce n'est **pas** un contrôle
 * d'accès : le serveur vérifie tout à chaque requête. Ici, ils ne servent qu'à
 * ne pas proposer une action qui serait refusée.
 */

import type { LibelleRole } from '@sygap/shared';

const CLE_STOCKAGE = 'sygap.session';

export interface Session {
  readonly jeton: string;
  /**
   * Identifiant du compte, lu dans `sub`. Sert à ne pas proposer les gestes
   * qu'un agent ne peut pas s'appliquer à lui-même — se désactiver, par
   * exemple. Ce n'est pas un contrôle d'accès : le serveur les refuse de son
   * côté.
   */
  readonly idUtilisateur: number | null;
  readonly login: string;
  readonly roles: readonly LibelleRole[];
  readonly idAdministration: number | null;
  /**
   * Sigle de l'administration de rattachement, affiché au bandeau. Tout l'écran
   * de l'agent est cloisonné par elle : ne pas la nommer laisse croire à une vue
   * complète du circuit.
   */
  readonly sigleAdministration: string | null;
  readonly idProfessionnel: number | null;
  /**
   * Le compte doit changer de mot de passe avant tout autre geste.
   *
   * Ne vient **pas** du JWT mais de la réponse de connexion : c'est un état du
   * compte, qui cesse d'être vrai dès le changement, alors que la charge d'un
   * jeton est figée à l'émission. L'y mettre obligerait à se reconnecter pour
   * que l'écran cesse de s'imposer.
   */
  readonly changementRequis: boolean;
  /** Horodatage d'expiration en millisecondes. */
  readonly expireLe: number;
}

interface ChargeJwt {
  readonly sub?: unknown;
  readonly login?: unknown;
  readonly roles?: unknown;
  readonly idAdministration?: unknown;
  readonly sigleAdministration?: unknown;
  readonly idProfessionnel?: unknown;
  readonly exp?: unknown;
}

/** Décode la charge d'un JWT sans vérifier sa signature — le serveur s'en charge. */
export function lireChargeJwt(jeton: string): ChargeJwt | null {
  const segments = jeton.split('.');
  const charge = segments[1];
  if (segments.length !== 3 || charge === undefined) {
    return null;
  }
  try {
    const base64 = charge.replace(/-/g, '+').replace(/_/g, '/');
    const binaire = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
    const octets = Uint8Array.from(binaire, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(octets)) as ChargeJwt;
  } catch {
    // Jeton illisible : traité comme une absence de session, jamais comme une
    // session partielle qui donnerait une interface incohérente.
    return null;
  }
}

/**
 * Construit la session à partir du jeton rendu par `/auth/connexion`.
 *
 * `changementRequis` accompagne le jeton dans la réponse de connexion : le
 * serveur seul sait si le mot de passe présenté était un provisoire.
 */
export function construireSession(jeton: string, changementRequis = false): Session | null {
  const charge = lireChargeJwt(jeton);
  if (charge === null || typeof charge.login !== 'string') {
    return null;
  }
  return {
    jeton,
    changementRequis,
    idUtilisateur: typeof charge.sub === 'number' ? charge.sub : null,
    login: charge.login,
    roles: Array.isArray(charge.roles) ? (charge.roles as LibelleRole[]) : [],
    idAdministration: typeof charge.idAdministration === 'number' ? charge.idAdministration : null,
    sigleAdministration:
      typeof charge.sigleAdministration === 'string' ? charge.sigleAdministration : null,
    idProfessionnel: typeof charge.idProfessionnel === 'number' ? charge.idProfessionnel : null,
    expireLe: typeof charge.exp === 'number' ? charge.exp * 1000 : 0,
  };
}

export function chargerSession(): Session | null {
  const brut = globalThis.sessionStorage.getItem(CLE_STOCKAGE);
  if (brut === null) return null;

  // Lu en `Partial` : ce qui sort du stockage n'est pas garanti complet — une
  // session écrite par une version antérieure n'a pas les champs ajoutés depuis.
  // Le typer `Session` ferait mentir le type là où il faut justement se méfier.
  let session: Partial<Session>;
  try {
    session = JSON.parse(brut) as Partial<Session>;
  } catch {
    globalThis.sessionStorage.removeItem(CLE_STOCKAGE);
    return null;
  }

  if (typeof session.jeton !== 'string' || (session.expireLe ?? 0) <= Date.now()) {
    globalThis.sessionStorage.removeItem(CLE_STOCKAGE);
    return null;
  }
  // Normalisé, et non lu tel quel : un `undefined` traité comme « changement
  // requis » enfermerait sur l'écran de mot de passe un agent qui n'a jamais
  // reçu de provisoire.
  return { ...(session as Session), changementRequis: session.changementRequis === true };
}

export function enregistrerSession(session: Session): void {
  globalThis.sessionStorage.setItem(CLE_STOCKAGE, JSON.stringify(session));
}

export function effacerSession(): void {
  globalThis.sessionStorage.removeItem(CLE_STOCKAGE);
}

export function aRole(session: Session | null, ...roles: readonly LibelleRole[]): boolean {
  if (session === null) return false;
  return session.roles.some((role) => roles.includes(role));
}

/**
 * Un compte « professionnel » sans rattachement à une administration relève du
 * portail. Le cumul des deux existe (un agent peut être armateur) : dans ce cas
 * le poste agent prime, et le portail reste accessible par son adresse.
 */
export function estProfessionnelSeul(session: Session): boolean {
  return aRole(session, 'professionnel') && session.idAdministration === null;
}

/**
 * Écran d'arrivée après connexion : un professionnel atterrit sur ses titres,
 * un agent sur sa file.
 *
 * Vit ici, et non dans `App`, pour que l'écran de connexion puisse l'appeler
 * sans créer de cycle d'import. Une adresse écrite en dur à l'atterrissage
 * enverrait tous les professionnels sur le poste agent.
 */
export function accueilDe(session: Session): string {
  return estProfessionnelSeul(session) ? '/mes-titres' : '/dossiers';
}
